import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, Package, Bell, TrendingUp } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { supabase } from '../lib/supabase'
import { getEventStatus } from '../lib/utils/eventStatus'
import EventCard from '../components/EventCard'
import LoadingSpinner from '../components/ui/LoadingSpinner'

interface DashboardStats {
  upcomingEventsCount: number
  activeOrdersCount: number
  totalOrdersCount: number
}

interface UpcomingEvent {
  id: string
  title: string
  location_name: string
  event_date: string
  status: string
}

interface RecentOrder {
  id: string
  order_number: string
  total_amount: number
  status: string
  created_at: string
  secret_events: {
    title: string
    event_date: string
  }
}

const DashboardPage: React.FC = () => {
  const { secretUser } = useAuth()
  const { unreadCount } = useNotifications()
  const [stats, setStats] = useState<DashboardStats>({
    upcomingEventsCount: 0,
    activeOrdersCount: 0,
    totalOrdersCount: 0
  })
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([])
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load dashboard data
  useEffect(() => {
    if (!secretUser) return

    loadDashboardData()
  }, [secretUser])

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)

      // Get upcoming events (fetch all non-draft, non-cancelled events)
      const { data: allEventsData, error: eventsError } = await supabase
        .from('secret_events')
        .select('*')
        .not('status', 'in', '("draft","cancelled")')
        .order('event_date', { ascending: true })

      if (eventsError) throw eventsError

      // Filter events using calculated status - show events customers should see:
      // - Scheduled: coming soon (can plan ahead)
      // - Active: enrollment open (can place orders)
      // - Enrollment Closed: enrollment ended but event hasn't started (already ordered)
      // - In Progress: event currently happening (need pickup info)
      const upcomingEvents = (allEventsData || []).filter(event => {
        const status = getEventStatus(event)
        return status === 'Active' || status === 'Scheduled' || status === 'Enrollment Closed' || status === 'In Progress'
      })

      // Get first 3 events for display
      const eventsData = upcomingEvents.slice(0, 3)
      const upcomingCount = upcomingEvents.length

      // Get user's active orders
      const { data: activeOrdersData, error: activeOrdersError } = await supabase
        .from('secret_orders')
        .select('id')
        .eq('user_id', secretUser.id)
        .in('status', ['pending', 'confirmed', 'preparing', 'ready'])

      if (activeOrdersError) throw activeOrdersError

      // Get user's total orders
      const { data: totalOrdersData, error: totalOrdersError } = await supabase
        .from('secret_orders')
        .select('id')
        .eq('user_id', secretUser.id)

      if (totalOrdersError) throw totalOrdersError

      // Get user's recent orders with event details
      const { data: ordersData, error: ordersError } = await supabase
        .from('secret_orders')
        .select(`
          id,
          order_number,
          total_amount,
          status,
          created_at,
          secret_events (
            title,
            event_date
          )
        `)
        .eq('user_id', secretUser.id)
        .order('created_at', { ascending: false })
        .limit(3)

      if (ordersError) throw ordersError

      // Update state
      setStats({
        upcomingEventsCount: upcomingCount || 0,
        activeOrdersCount: activeOrdersData?.length || 0,
        totalOrdersCount: totalOrdersData?.length || 0
      })

      setUpcomingEvents(eventsData || [])
      setRecentOrders(ordersData || [])

    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getOrderStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
      confirmed: { label: 'Confirmed', color: 'bg-green-500/20 text-green-400' },
      preparing: { label: 'Preparing', color: 'bg-blue-500/20 text-blue-400' },
      ready: { label: 'Ready', color: 'bg-purple-500/20 text-purple-400' },
      completed: { label: 'Completed', color: 'bg-gray-500/20 text-gray-400' },
      cancelled: { label: 'Cancelled', color: 'bg-red-500/20 text-red-400' },
      refunded: { label: 'Refunded', color: 'bg-orange-500/20 text-orange-400' },
    }

    const config = statusConfig[status] || statusConfig.pending

    return (
      <span className={`text-xs px-2 py-1 rounded-full ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatEventDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen-safe bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      {/* Main Content */}
      <div className="flex-1 px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-black text-foreground mb-2">
              {secretUser?.previous_sign_in ? 'Welcome back' : 'Welcome'}, {secretUser?.first_name || 'Member'}
            </h1>
            <p className="text-muted-foreground">
              Your SECRET dashboard • Manage your exclusive access
            </p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secret-gradient rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.upcomingEventsCount}</p>
                  <p className="text-sm text-muted-foreground">Upcoming Events</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secret-gradient rounded-lg flex items-center justify-center">
                  <Package className="w-6 h-6 text-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.activeOrdersCount}</p>
                  <p className="text-sm text-muted-foreground">Active Orders</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secret-gradient rounded-lg flex items-center justify-center">
                  <Bell className="w-6 h-6 text-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{unreadCount}</p>
                  <p className="text-sm text-muted-foreground">New Notifications</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-secret-gradient rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-foreground" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stats.totalOrdersCount}</p>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Upcoming Events */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">Upcoming Events</h2>
                <Link to="/events" className="text-primary hover:text-primary/80 text-sm font-medium">
                  View All
                </Link>
              </div>

              {upcomingEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No upcoming events yet</p>
                  <p className="text-sm text-muted-foreground">
                    You'll be notified when new SECRET events are scheduled
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingEvents.map(event => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              )}
            </div>

            {/* Recent Orders */}
            <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-foreground">Recent Orders</h2>
                <Link to="/orders" className="text-primary hover:text-primary/80 text-sm font-medium">
                  View All
                </Link>
              </div>

              {recentOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">No orders yet</p>
                  <p className="text-sm text-muted-foreground">
                    Your pre-orders will appear here once you place them
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOrders.map(order => (
                    <Link
                      key={order.id}
                      to={`/orders/${order.id}`}
                      className="block bg-background rounded-lg p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground mb-1">
                            Order #{order.order_number}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {order.secret_events?.title || 'Event'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-foreground mb-1">
                            ${order.total_amount.toFixed(2)}
                          </p>
                          {getOrderStatusBadge(order.status)}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Member Status */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm mt-8">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-foreground mb-1">SECRET Member Status</h3>
                <p className="text-muted-foreground">
                  Active since {secretUser?.created_at ? new Date(secretUser.created_at).toLocaleDateString() : 'Today'}
                </p>
              </div>
              <div className="text-right">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/20 text-primary">
                  Active Member
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage